import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import ElementQuery from '../../ElementQuery/ElementQuery';
import Lightbox from '../../Lightbox/Lightbox';
import Modal from '../../Modal/Modal';
import TooltipSelect from '../Select/Select';
import { getLeftOffset } from '../helpers';

import styles from './Controller.scss';
import mediaQueries from 'wanderu-css/mediaQueries.scss';

class TooltipController extends React.Component {
    static propTypes = {
        breakpoint: PropTypes.number,
        children: PropTypes.node.isRequired,
        className: PropTypes.string,
        contentClassName: PropTypes.string,
        noHover: PropTypes.bool,
        noModal: PropTypes.bool,
        offsetX: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        offsetY: PropTypes.number,
        onClick: PropTypes.func,
        onHover: PropTypes.func,
        placement: PropTypes.oneOf(['top', 'bottom']),
    };

    static defaultProps = {
        breakpoint: parseInt(mediaQueries.maxWidthSmallScreen, 10),
        className: '',
        noHover: false,
        noModal: false,
        offsetX: 0,
        offsetY: 0,
        onClick: () => undefined,
        onHover: () => undefined,
        placement: 'top',
    };

    getPositionTimeout = null;

    showTooltipTimeout = null;

    constructor(props) {
        super(props);

        this.tooltip = React.createRef();
        this.queryElement = typeof window !== 'undefined'
            ? document.body
            : null;

        this.state = {
            queries: this.buildQueryMap(),
            showModal: false,
            showTooltip: false,
            style: {
                left: 0,
                position: 'absolute',
                top: 0,
            },
            tooltipWidth: 0,
            useModal: true,
            useTooltip: true,
        };
    }

    getPosition = (elementBounding) => {
        this.getPositionTimeout = setTimeout(() => {
            const { offsetX, offsetY } = this.props;
            const { tooltipWidth } = this.state;
            const {
                top, left, height, width,
            } = elementBounding;
            const topOffset = top
                + height
                + offsetY
                + (window.pageYOffset
                    || document.documentElement.scrollTop
                    || document.body.scrollTop
                    || 0);

            this.setState((state) => ({
                style: {
                    ...state.style,
                    left: getLeftOffset({
                        left,
                        offset: offsetX,
                        tooltipWidth,
                        width,
                    }),
                    top: topOffset,
                },
            }));
        }, 0);
    };

    componentDidUpdate(prevProps, prevState) {
        this.showTooltipTimeout = setTimeout(() => {
            if (this.state.showTooltip && !prevState.showTooltip) {
                window.addEventListener('touchend', this.onMouseLeave);
                window.addEventListener('click', this.onMouseLeave);
            } else {
                window.removeEventListener('touchend', this.onMouseLeave);
                window.removeEventListener('click', this.onMouseLeave);
            }
        }, 0);

        if (prevProps.breakpoint !== this.props.breakpoint) {
            this.setState(() => ({
                queries: this.buildQueryMap(),
            }));
        }
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.onMouseLeave);
        window.removeEventListener('touchend', this.onMouseLeave);
        clearTimeout(this.getPositionTimeout);
        clearTimeout(this.showTooltipTimeout);
    }

    onMouseEnter = (e, resize) => {
        if (!this.props.noHover && this.state.useTooltip) {
            if (resize) resize();

            this.props.onHover();

            return this.setState(
                () => ({ showTooltip: true }),
                () => {
                    this.setState(() => ({
                        tooltipWidth: this.tooltip.current?.getBoundingClientRect().width || 0,
                    }));
                },
            );
        }

        return null;
    };

    onMouseLeave = () => {
        if (this.state.useTooltip) this.setState(() => ({ showTooltip: false }));
    };

    onClick = (e) => {
        if (!this.state.useModal || this.state.showModal || this.props.noModal) {
            // don't care, or already open
            return;
        }

        this.props.onClick();
        e.preventDefault();
        e.stopPropagation();

        this.setState(() => {
            return {
                showModal: true,
            };
        });
    };

    onHideModal = (e) => {
        // no event is generated when a Lightbox is the child and its X is clicked
        if (e) {
            e.stopPropagation();
        }

        this.setState(() => {
            return {
                showModal: false,
            };
        });
    };

    buildQueryMap() {
        const { breakpoint } = this.props;

        return {
            useModal: {
                maxWidth: breakpoint,
            },
            useTooltip: {
                minWidth: breakpoint + 1,
            },
        };
    }

    onElementQueryChange = (queryResults) => {
        this.setState(() => {
            return queryResults;
        });
    };

    render() {
        const {
            children,
            className,
            contentClassName,
            noHover,
            noModal,
            placement,
        } = this.props;

        const {
            queries,
            showModal,
            showTooltip,
            style,
            useModal,
            useTooltip,
        } = this.state;

        return React.Children.map(children, (child) => {
            if (!child) return null;

            if (child.type.displayName === TooltipSelect.displayName) {
                return React.cloneElement(child, {
                    getPosition: this.getPosition,
                    noHover: noHover,
                    noModal: noModal,
                    onClick: this.onClick,
                    onMouseEnter: this.onMouseEnter,
                    onMouseLeave: this.onMouseLeave,
                    showModal: showModal,
                    useModal: useModal,
                    useTooltip: useTooltip,
                });
            }

            return (
                <ElementQuery
                    element={this.queryElement}
                    onChange={this.onElementQueryChange}
                    passAsProps={false}
                    queries={queries}
                >
                    <React.Fragment>
                        {!noHover && useTooltip && showTooltip && ReactDOM.createPortal(
                            <div
                                className={classNames(
                                    styles.tooltipContainer,
                                    className,
                                    {
                                        [styles.top]: placement === 'top',
                                        [styles.bottom]: placement === 'bottom',
                                    },
                                )}
                                onMouseEnter={this.onMouseEnter}
                                onMouseLeave={this.onMouseLeave}
                                onTouchEnd={(e) => e.stopPropagation()}
                                ref={this.tooltip}
                                role="tooltip"
                                style={style}
                            >
                                <div className={classNames(
                                    styles.tooltipContent,
                                    contentClassName,
                                    {
                                        [styles.top]: placement === 'top',
                                        [styles.bottom]: placement === 'bottom',
                                    },
                                )}>
                                    {React.cloneElement(child)}
                                </div>
                            </div>,
                            document.body,
                        )}

                        {!noModal && useModal && showModal && (
                            <Modal>
                                <Lightbox
                                    className={className}
                                    onHide={this.onHideModal}
                                >
                                    {React.cloneElement(child)}
                                </Lightbox>
                            </Modal>
                        )}
                    </React.Fragment>
                </ElementQuery>
            );
        });
    }
}

export {
    TooltipController,
    getLeftOffset,
};

export default TooltipController;
